/**
 * Delivery Estimator
 *
 * Web component dat de levertijd berekent op basis van:
 * - Huidige tijd in Europe/Amsterdam timezone
 * - Cut-off tijd (default 21:00)
 * - Verzenddagen (ma-za, geen zondag)
 * - Nederlandse feestdagen (vast + Pasen-berekend)
 * - Transit-dagen min/max per land
 *
 * Update zichzelf elke 60 seconden zodat de cut-off live klopt.
 */

class DeliveryEstimator extends HTMLElement {
  static DAY_NAMES = {
    nl: {
      today: 'vandaag',
      tomorrow: 'morgen',
      weekdays: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
      months: ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
               'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
    },
    en: {
      today: 'today',
      tomorrow: 'tomorrow',
      weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      months: ['January', 'February', 'March', 'April', 'May', 'June',
               'July', 'August', 'September', 'October', 'November', 'December'],
    },
    fr: {
      today: "aujourd'hui",
      tomorrow: 'demain',
      weekdays: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
               'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
    },
    de: {
      today: 'heute',
      tomorrow: 'morgen',
      weekdays: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
               'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    },
  };

  connectedCallback() {
    this.update();
    this._interval = setInterval(() => this.update(), 60_000);
  }

  disconnectedCallback() {
    if (this._interval) clearInterval(this._interval);
  }

  amsterdamNow() {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    return new Date(fmt.format(now).replace(' ', 'T'));
  }

  easterDate(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  getDutchHolidays(year) {
    const easter = this.easterDate(year);
    const offset = (days) => {
      const d = new Date(easter);
      d.setDate(easter.getDate() + days);
      return d;
    };

    const apr27 = new Date(year, 3, 27);
    const kingsDay = apr27.getDay() === 0 ? new Date(year, 3, 26) : apr27;

    const holidays = [
      new Date(year, 0, 1),    // Nieuwjaarsdag
      offset(-2),              // Goede Vrijdag
      offset(1),               // 2e Paasdag
      kingsDay,                // Koningsdag
      offset(39),              // Hemelvaartsdag
      offset(50),              // 2e Pinksterdag
      new Date(year, 11, 25),  // 1e Kerstdag
      new Date(year, 11, 26),  // 2e Kerstdag
    ];

    return holidays.map((d) => this.toISODate(d));
  }

  toISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  isShippingDay(date) {
    const day = date.getDay();
    if (day === 0) return false;
    const holidays = this.getDutchHolidays(date.getFullYear());
    return !holidays.includes(this.toISODate(date));
  }

  isDeliveryDay(date, saturdayEnabled) {
    const day = date.getDay();
    if (day === 0) return false;
    if (day === 6 && !saturdayEnabled) return false;
    const holidays = this.getDutchHolidays(date.getFullYear());
    return !holidays.includes(this.toISODate(date));
  }

  findShippingDate(now, beforeCutoff) {
    const ship = new Date(now);
    if (!beforeCutoff) {
      ship.setDate(ship.getDate() + 1);
    }
    while (!this.isShippingDay(ship)) {
      ship.setDate(ship.getDate() + 1);
    }
    return ship;
  }

  findDeliveryDate(shipDate, transitDays, saturdayEnabled) {
    const delivery = new Date(shipDate);
    let added = 0;
    while (added < transitDays) {
      delivery.setDate(delivery.getDate() + 1);
      if (this.isDeliveryDay(delivery, saturdayEnabled)) {
        added++;
      }
    }
    return delivery;
  }

  /**
   * Geeft een leesbare dag-label voor één datum.
   * - Vandaag → "vandaag"
   * - Morgen → "morgen"
   * - 2-6 dagen weg → dagnaam ("woensdag")
   * - 7+ dagen weg → dagnaam + datum + maand ("maandag 28 december")
   */
  formatDayLabel(date, locale) {
    const labels = DeliveryEstimator.DAY_NAMES[locale] || DeliveryEstimator.DAY_NAMES.nl;
    const diffDays = this.diffDaysFromToday(date);

    if (diffDays === 0) return labels.today;
    if (diffDays === 1) return labels.tomorrow;

    if (diffDays >= 2 && diffDays < 7) {
      return labels.weekdays[date.getDay()];
    }

    return `${labels.weekdays[date.getDay()]} ${date.getDate()} ${labels.months[date.getMonth()]}`;
  }

  /**
   * Geeft altijd een dagnaam terug (geen "morgen" of "vandaag"), met datum
   * als de dag verder dan een week weg is. Gebruikt voor range-weergave
   * waar "morgen/dinsdag" raar zou lezen.
   */
  formatDayName(date, locale) {
    const labels = DeliveryEstimator.DAY_NAMES[locale] || DeliveryEstimator.DAY_NAMES.nl;
    const diffDays = this.diffDaysFromToday(date);

    if (diffDays < 7) {
      return labels.weekdays[date.getDay()];
    }

    return `${labels.weekdays[date.getDay()]} ${date.getDate()} ${labels.months[date.getMonth()]}`;
  }

  /**
   * Formatteert een range. Als min en max dezelfde dag zijn, gebruik
   * formatDayLabel (kan "morgen" tonen). Anders altijd dagnamen met slash.
   */
  formatDayRange(dateMin, dateMax, locale) {
    if (this.sameDay(dateMin, dateMax)) {
      return this.formatDayLabel(dateMin, locale);
    }
    const minStr = this.formatDayName(dateMin, locale);
    const maxStr = this.formatDayName(dateMax, locale);
    return `${minStr}/${maxStr}`;
  }

  sameDay(d1, d2) {
    return d1.getFullYear() === d2.getFullYear()
      && d1.getMonth() === d2.getMonth()
      && d1.getDate() === d2.getDate();
  }

  diffDaysFromToday(date) {
    const now = this.amsterdamNow();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return Math.round((targetMidnight - todayMidnight) / (1000 * 60 * 60 * 24));
  }

  update() {
    const cutoff = this.dataset.cutoff || '21:00';
    const transitMin = parseInt(this.dataset.transitMin, 10) || 1;
    const transitMax = parseInt(this.dataset.transitMax, 10) || transitMin;
    const saturdayEnabled = this.dataset.saturday !== 'false';
    const locale = (this.dataset.locale || 'nl').toLowerCase();

    const now = this.amsterdamNow();
    const [cutoffHour, cutoffMinute] = cutoff.split(':').map(Number);
    const cutoffToday = new Date(now);
    cutoffToday.setHours(cutoffHour, cutoffMinute, 0, 0);

    const beforeCutoff = now < cutoffToday;
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    const todayIsHoliday = this.getDutchHolidays(now.getFullYear()).includes(this.toISODate(now));
    const shippingPossibleToday = beforeCutoff && this.isShippingDay(now);

    const shipDate = this.findShippingDate(now, beforeCutoff);
    const deliveryMin = this.findDeliveryDate(shipDate, transitMin, saturdayEnabled);
    const deliveryMax = this.findDeliveryDate(shipDate, transitMax, saturdayEnabled);

    const dayLabel = this.formatDayRange(deliveryMin, deliveryMax, locale);
    const shipDayLabel = this.formatDayLabel(shipDate, locale);

    let template;
    if (shippingPossibleToday) {
      template = this.dataset.textBefore;
    } else if (isWeekend || todayIsHoliday) {
      template = this.dataset.textWeekend;
    } else {
      template = this.dataset.textAfter;
    }

    const filledText = template
      .replace('%time%', cutoff)
      .replace('%day%', dayLabel)
      .replace('%shipday%', shipDayLabel);

    const finalText = filledText.charAt(0).toUpperCase() + filledText.slice(1);

    const textEl = this.querySelector('[data-text]');
    if (textEl) textEl.textContent = finalText;
  }
}

if (!customElements.get('delivery-estimator')) {
  customElements.define('delivery-estimator', DeliveryEstimator);
}